import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Person,
  Palette,
  Language as LanguageIcon,
  Lock,
  LightMode,
  DarkMode,
  Visibility,
  VisibilityOff,
  Save,
  ArrowBack,
  DeleteForever,
  Warning,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useLanguage, languageLabels } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import client from '../services/apiClient';

const SettingsPage = () => {
  const navigate = useNavigate();
  const { themeMode, setTheme } = useTheme();
  const { language, setLanguage, t, languages } = useLanguage();
  const { user, refreshUser } = useAuth();

  // Profile state
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState('');
  const [profileError, setProfileError] = useState('');

  // Password state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Delete account state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE MY ACCOUNT') return;
    setDeleteLoading(true);
    setDeleteError('');
    try {
      await client.delete('/api/v1/privacy/account', {
        data: { confirmation: 'DELETE MY ACCOUNT', reason: deleteReason || null },
      });
      // Account deleted — clear local state and redirect to login
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    } catch (error) {
      setDeleteError(error.response?.data?.detail || 'Failed to delete account. Please try again.');
      setDeleteLoading(false);
    }
  };

  // Load user data on mount
  useEffect(() => {
    if (user) {
      setUsername(user.username || '');
      setEmail(user.email || '');
    }
  }, [user]);

  // Handle profile update
  const handleProfileUpdate = async () => {
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      await client.put('/api/v1/auth/me', {
        username: username,
        language: language,
        theme: themeMode,
      });

      setProfileSuccess(t('profileUpdated'));
      if (refreshUser) {
        await refreshUser();
      }
    } catch (error) {
      setProfileError(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Handle password change
  const handlePasswordChange = async () => {
    setPasswordLoading(true);
    setPasswordError('');
    setPasswordSuccess('');

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      setPasswordLoading(false);
      return;
    }

    if (newPassword.length < 12) {
      setPasswordError('Password must be at least 12 characters');
      setPasswordLoading(false);
      return;
    }

    try {
      await client.post('/api/v1/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });

      setPasswordSuccess(t('passwordUpdated'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Handle theme change
  const handleThemeChange = (event, newTheme) => {
    if (newTheme !== null) {
      setTheme(newTheme);
    }
  };

  // Handle language change
  const handleLanguageChange = (event) => {
    setLanguage(event.target.value);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{ mr: 2 }}
          aria-label="Go back"
        >
          <ArrowBack />
        </IconButton>
        <Typography variant="h4">
          {t('settingsTitle')}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Profile Section */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <Person />
                </Avatar>
                <Typography variant="h6">{t('profileSettings')}</Typography>
              </Box>

              {profileSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setProfileSuccess('')}>
                  {profileSuccess}
                </Alert>
              )}
              {profileError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setProfileError('')}>
                  {profileError}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('username')}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('email')}
                    value={email}
                    disabled
                    variant="outlined"
                    helperText="Email cannot be changed"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    startIcon={profileLoading ? <CircularProgress size={20} /> : <Save />}
                    onClick={handleProfileUpdate}
                    disabled={profileLoading}
                  >
                    {t('updateProfile')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Appearance Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <Palette />
                </Avatar>
                <Typography variant="h6">{t('appearance')}</Typography>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('theme')}
              </Typography>

              <ToggleButtonGroup
                value={themeMode}
                exclusive
                onChange={handleThemeChange}
                fullWidth
              >
                <ToggleButton value="light">
                  <LightMode sx={{ mr: 1 }} />
                  {t('lightMode')}
                </ToggleButton>
                <ToggleButton value="dark">
                  <DarkMode sx={{ mr: 1 }} />
                  {t('darkMode')}
                </ToggleButton>
              </ToggleButtonGroup>
            </CardContent>
          </Card>
        </Grid>

        {/* Language Section */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'info.main', mr: 2 }}>
                  <LanguageIcon />
                </Avatar>
                <Typography variant="h6">{t('languageSettings')}</Typography>
              </Box>

              <FormControl fullWidth>
                <InputLabel>{t('language')}</InputLabel>
                <Select
                  value={language}
                  onChange={handleLanguageChange}
                  label={t('language')}
                >
                  {languages.map((lang) => (
                    <MenuItem key={lang} value={lang}>
                      {languageLabels[lang]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Security Section - Change Password */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                  <Lock />
                </Avatar>
                <Typography variant="h6">{t('changePassword')}</Typography>
              </Box>

              {passwordSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPasswordSuccess('')}>
                  {passwordSuccess}
                </Alert>
              )}
              {passwordError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPasswordError('')}>
                  {passwordError}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={t('currentPassword')}
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                            edge="end"
                          >
                            {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('newPassword')}
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    variant="outlined"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            edge="end"
                          >
                            {showNewPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label={t('confirmPassword')}
                    type={showNewPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    variant="outlined"
                    error={confirmPassword && newPassword !== confirmPassword}
                    helperText={
                      confirmPassword && newPassword !== confirmPassword
                        ? 'Passwords do not match'
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={passwordLoading ? <CircularProgress size={20} /> : <Lock />}
                    onClick={handlePasswordChange}
                    disabled={passwordLoading || !currentPassword || !newPassword || !confirmPassword}
                  >
                    {t('updatePassword')}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Danger Zone */}
      <Card sx={{ mt: 3, border: '1px solid', borderColor: 'error.main' }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
              <Warning />
            </Avatar>
            <Typography variant="h6" color="error">Danger Zone</Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="body1" fontWeight={600}>Delete Account</Typography>
              <Typography variant="body2" color="text.secondary">
                Permanently delete your account and all associated data. This action cannot be undone.
              </Typography>
            </Box>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteForever />}
              onClick={() => { setDeleteDialogOpen(true); setDeleteConfirmation(''); setDeleteReason(''); setDeleteError(''); }}
              sx={{ minWidth: 160, textTransform: 'none' }}
            >
              Delete Account
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleteLoading && setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
          <DeleteForever /> Delete Account
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete your account and <strong>ALL</strong> your data:
          </Alert>
          <Box sx={{ mb: 2, pl: 2 }}>
            <Typography variant="body2" color="text.secondary">
              - All projects and their contents<br />
              - All documents, CAD drawings, and files<br />
              - All requirements and compliance standards<br />
              - All test cases, inspection checklists, and verification data<br />
              - All chat history and AI interactions<br />
              - All uploaded files from cloud storage<br />
              - Your authentication account
            </Typography>
          </Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action is <strong>irreversible</strong>. There is no recovery.
          </Alert>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
          )}
          <TextField
            fullWidth
            label='Type "DELETE MY ACCOUNT" to confirm'
            value={deleteConfirmation}
            onChange={(e) => setDeleteConfirmation(e.target.value)}
            error={deleteConfirmation.length > 0 && deleteConfirmation !== 'DELETE MY ACCOUNT'}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Reason for leaving (optional)"
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteAccount}
            disabled={deleteConfirmation !== 'DELETE MY ACCOUNT' || deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForever />}
          >
            {deleteLoading ? 'Deleting...' : 'Permanently Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SettingsPage;
